import React, { useState } from 'react'
import { Dialog, Box, Typography, Button, TextField } from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { Spinner } from 'react-bootstrap'
import { styled } from '@mui/material/styles'
import dayjs from 'dayjs'

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: 0,
    maxWidth: '480px',
  },
}))

const DialogContent = styled(Box)`
  padding: 24px;
`

const InfoCard = styled(Box)`
  margin-bottom: 24px;
  padding: 16px;
  background: rgba(16, 185, 129, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.2);
`

const ActionButtons = styled(Box)`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`

const CancelButton = styled(Button)`
  padding: 10px 24px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: #fff;
  color: rgba(0, 0, 0, 0.85);
  font-size: 14px;
  font-weight: 600;
  text-transform: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ConfirmButton = styled(Button)`
  padding: 10px 24px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: linear-gradient(135deg, rgba(16, 185, 129, 1) 0%, rgba(5, 150, 105, 1) 100%);
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.35);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

export const ConfirmMarkCompletedDialog = ({
  open,
  onClose,
  sessionData,
  onConfirm,
  markBookedSessionAsCompletedFunction,
  refreshBookedSlots,
}) => {
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      if (markBookedSessionAsCompletedFunction && sessionData) {
        await markBookedSessionAsCompletedFunction(sessionData, refreshBookedSlots)
        if (onClose) {
          onClose()
        }
      } else if (onConfirm) {
        await onConfirm()
        if (onClose) {
          onClose()
        }
      }
    } catch (error) {
      console.error('Error marking session as completed:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (onClose) {
      onClose()
    }
  }

  // Handle both boolean and object types for open prop
  const isOpen = typeof open === 'boolean' ? open : Boolean(open)

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleCancel}
      aria-labelledby="mark-completed-dialog-title"
    >
      <DialogContent>
        <Box sx={{ marginBottom: '20px' }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: 'rgba(0, 0, 0, 0.85)',
              margin: '0 0 8px 0',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Mark as Completed
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: 'rgba(0, 0, 0, 0.6)',
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to mark this appointment as completed?
          </Typography>
        </Box>

        <InfoCard>
          <Typography
            sx={{
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
              color: 'rgba(0, 0, 0, 0.85)',
            }}
          >
            Patient: {sessionData?.userName || 'N/A'}
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              color: 'rgba(0, 0, 0, 0.7)',
            }}
          >
            {sessionData?.startTime && sessionData?.endTime && (
              <>
                {dayjs.utc(sessionData.startTime).local().format('DD MMM YYYY, h:mm A')} -{' '}
                {dayjs.utc(sessionData.endTime).local().format('h:mm A')}
              </>
            )}
          </Typography>
        </InfoCard>

        <ActionButtons>
          <CancelButton onClick={handleCancel} disabled={loading}>
            Cancel
          </CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" variant="light" size="sm" />
                Marking...
              </>
            ) : (
              <>
                <CheckCircle sx={{ fontSize: 18 }} />
                Mark as Completed
              </>
            )}
          </ConfirmButton>
        </ActionButtons>
      </DialogContent>
    </StyledDialog>
  )
}

